// src/pages/.../IntakeFormBuilder.jsx

import FixedFieldSelect from "../../components/adresses/intakeforms/FixedFieldSelect.jsx";
import Location from "../../hooks/Location";
import PageHeader from "../../components/layout/PageHeader.jsx";
import notDeleted from "../../helpers/notDeleted.js";
import {
  useFirestore,
  useFirestoreGeneralOrderBy,
  useFirestoreID,
} from "../../firebase/useFirestore";
import { useContext, useMemo, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import { client } from "../../hooks/Client";
import { db, timestamp } from "../../firebase/config.js";
import uuid from "react-uuid";
import Tooltip from "../../components/common/Tooltip";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ToggleButton from "../../components/common/ToggleButton";
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Modal from "../../components/common/Modal";
import OptionsModalContent from "../../components/adresses/intakeforms/OptionsModalContent.jsx";
import IntakeFormMatrixEditor from "../../components/adresses/intakeforms/IntakeFormMatrixEditor.jsx";
import SupportFormOptionsCount from "../../components/adresses/intakeforms/SupportFormOptionsCount";
import DatalistOptions from "../../components/adresses/data/DatalistOptions";
import DatalistOptionsPersonas from "../../components/personas/DataListOptionsPersonas";
import Table from "../../components/common/table/Table.jsx";
import TableCell from "../../components/common/table/TableCell.jsx";
import TableDragHandleCell from "../../components/common/table/TableDragHandleCell.jsx";
import useSettings from "../../hooks/Settings";
import ReadOnlyGuard from "../../components/common/ReadOnlyGuard";
import { INTAKE_FORM_FIELD_TYPES } from "../../helpers/dataFields/dataFieldTypes";

const IntakeFormBuilder = () => {
  // Context
  const [, setSaved] = useContext(SavedIcon);

  // State
  const [formType, setFormType] = useState(null);
  const [formTarget, setFormTarget] = useState(null);
  const [formSelected, setFormSelected] = useState(null);
  const [allPersonasAndAdresses, setAllPersonasAndAdresses] = useState([]);
  const [dragArrayFormFields, setDragArrayFormFields] = useState([]);

  // Options modal (field-specific)
  const [optionsModalOpen, setOptionsModalOpen] = useState(false);
  const [activeOptionsField, setActiveOptionsField] = useState(null);

  // Matrix modal (rows/columns of a matrix field)
  const [matrixModalOpen, setMatrixModalOpen] = useState(false);
  const [activeMatrixField, setActiveMatrixField] = useState(null);


  // Hooks
  const formId = Location()[3];
  const { personasOn, housevisits } = useSettings();
  const history = useHistory();

  // Firestore
  const intakeForm = useFirestoreID("IntakeForms", formId ? formId : "");
  const compagny = useFirestore("CompagnyMeta");
  const privacyConfigured =
    !!compagny?.[0]?.PrivacyLinkLabel && !!compagny?.[0]?.PrivacyLinkUrl;
  const formFields = useFirestoreGeneralOrderBy(
    "IntakeFormFields",
    "FormId",
    formId ? formId : "",
    "Position",
    "asc"
  );
  const personas = useFirestore("Personas");
  // Soft delete (ADR-008): verwijderde huishoudens niet aanbieden.
  // AI-CONTEXT: See docs/pitfalls.md#state-useeffect-inline-filter
  // `adresses` is een dependency van de useEffect verderop — memoiseren, anders
  // is het elke render een nieuwe array en vuurt die effect eindeloos.
  const allAdresses = useFirestore("Adresses");
  const adresses = useMemo(
    () => (Array.isArray(allAdresses) ? allAdresses : []).filter(notDeleted),
    [allAdresses]
  );

  // Set firestore target to state
  useEffect(() => {
    if (!intakeForm || !intakeForm[0]) return;
    setFormTarget(intakeForm[0]?.FormTarget || null);
  }, [intakeForm]);

  // Auto-set FormTarget when only one of personas/housevisits is enabled
  useEffect(() => {
    if (!intakeForm || !intakeForm[0]) return;
    if (intakeForm[0].FormType !== "intake") return;

    const onlyPersonas = personasOn === 'flex' && !housevisits;
    const onlyHousevisits = housevisits && personasOn !== 'flex';
    const desired = onlyPersonas ? 'persona' : onlyHousevisits ? 'adress' : null;

    if (!desired || intakeForm[0].FormTarget === desired) return;
    db.collection("IntakeForms")
      .doc(intakeForm[0].docid)
      .update({ FormTarget: desired });
  }, [intakeForm, personasOn, housevisits]);

  // -----------------------------
  // Auto rules for "Type woning"
  // -----------------------------
  const HOUSING_TITLE = "Type woning";
  const HOUSING_OPTIONS = ["koop", "sociale huur", "particuliere huur"];

  const normalize = (v) => (v || "").trim();

  // -----------------------------
  // Options modal open/close
  // -----------------------------
  const openOptionsModal = (field) => {
    setActiveOptionsField(field);
    setOptionsModalOpen(true);
  };

  const closeOptionsModal = () => {
    setOptionsModalOpen(false);
    setActiveOptionsField(null);
  };

  const openMatrixModal = (field) => {
    setActiveMatrixField(field);
    setMatrixModalOpen(true);
  };

  const closeMatrixModal = () => {
    setMatrixModalOpen(false);
    setActiveMatrixField(null);
  };

  const softDeleteMultipleOptionsByFieldId = async (fieldId) => {
    if (!fieldId) return;

    const snap = await db
      .collection("MultipleQuestionOptions")
      .where("Field", "==", fieldId)
      .get();

    if (snap.empty) return;

    const updates = [];
    snap.forEach((doc) => {
      updates.push(
        db.collection("MultipleQuestionOptions").doc(doc.id).update({
          Deleted: "true",
          DeletedAt: timestamp,
        })
      );
    });

    await Promise.all(updates);
    setSaved("flex");
  };

  // The matrix counterpart of softDeleteMultipleOptionsByFieldId: without this,
  // deleting a matrix field (or switching it to another type) would leave its
  // rows and columns alive in Firestore, keyed to a field ID nothing points at.
  const softDeleteMatrixByFieldId = async (fieldId) => {
    if (!fieldId) return;

    const retire = (collection, docId) =>
      db.collection(collection).doc(docId).update({
        Deleted: "true",
        DeletedAt: timestamp,
      });

    const findByField = (collection, id) =>
      db
        .collection(collection)
        .where("CompagnyID", "==", client)
        .where("Field", "==", id)
        .get();

    const [rowSnap, columnSnap] = await Promise.all([
      findByField("MatrixQuestionRows", fieldId),
      findByField("MatrixQuestionColumns", fieldId),
    ]);

    // A 'choice' column's answer options hang off the COLUMN's ID, not the
    // field's — so softDeleteMultipleOptionsByFieldId does not reach them.
    const optionSnaps = await Promise.all(
      columnSnap.docs.map((doc) =>
        findByField("MultipleQuestionOptions", doc.data().ID)
      )
    );

    await Promise.all([
      ...rowSnap.docs.map((doc) => retire("MatrixQuestionRows", doc.id)),
      ...columnSnap.docs.map((doc) => retire("MatrixQuestionColumns", doc.id)),
      ...optionSnaps.flatMap((snap) =>
        snap.docs.map((doc) => retire("MultipleQuestionOptions", doc.id))
      ),
    ]);

    setSaved("flex");
  };

  const ensureHousingTypeOptions = async (field) => {
    if (!field?.ID) return;

    const snap = await db
      .collection("MultipleQuestionOptions")
      .where("Field", "==", field.ID)
      .get();

    const existing = [];
    snap.forEach((d) => existing.push({ docid: d.id, ...d.data() }));

    // Active = not soft-deleted
    const active = existing.filter((o) => (o?.Deleted || "false") !== "true");
    const activeLower = active
      .map((o) => normalize(o?.Option).toLowerCase())
      .filter(Boolean);

    // Deleted options by label => revive instead of duplicating
    const deletedByLabel = {};
    existing
      .filter((o) => (o?.Deleted || "false") === "true")
      .forEach((o) => {
        const key = normalize(o?.Option).toLowerCase();
        if (key) deletedByLabel[key] = o;
      });

    let nextPos = (active?.length || 0) + 1;

    const ops = [];

    HOUSING_OPTIONS.forEach((label) => {
      const key = label.toLowerCase();

      if (activeLower.includes(key)) return;

      // revive
      if (deletedByLabel[key]?.docid) {
        ops.push(
          db
            .collection("MultipleQuestionOptions")
            .doc(deletedByLabel[key].docid)
            .update({
              Deleted: "false",
              DeletedAt: "",
              Position: nextPos++,
              Question: HOUSING_TITLE,
            })
        );
        return;
      }

      // create new
      ops.push(
        db.collection("MultipleQuestionOptions").add({
          Option: label,
          Question: HOUSING_TITLE,
          Field: field.ID,
          Compagny: client,
          CompagnyID: client,
          Timestamp: timestamp,
          Position: nextPos++,
          ID: uuid(),
          Deleted: "false",
          DeletedAt: "",
        })
      );
    });

    if (!ops.length) return;

    await Promise.all(ops);
    setSaved("flex");
  };

  // AI-CONTEXT: See docs/patterns.md#ui-intake-form-field-type
  // AI-CONTEXT: See docs/pitfalls.md#state-handler-overwrites-sibling-field
  const applyAutoTypeRules = async ({ fieldDocid, field, nextTitle }) => {
    if (!fieldDocid) return;

    const normalizedTitle = normalize(nextTitle);
    const wasHousing = normalize(field?.Title) === HOUSING_TITLE;
    const isHousing = normalizedTitle === HOUSING_TITLE;

    // Only the "Type woning" special case touches Type. A normal title edit
    // must NOT overwrite a manually chosen type (checkbox, notitie, etc.).
    const update = { Title: normalizedTitle };
    if (isHousing) {
      update.Type = "multiple-one";
    } else if (wasHousing) {
      update.Type = "text";
    }

    await db.collection("IntakeFormFields").doc(fieldDocid).update(update);

    // left "Type woning" => soft delete its (housing) options
    if (wasHousing && !isHousing && field?.ID) {
      await softDeleteMultipleOptionsByFieldId(field.ID);
    }

    // moved to "Type woning" => ensure options exist
    if (isHousing) {
      await ensureHousingTypeOptions(field);
    }

    setSaved("flex");
  };

  // -----------------------------
  // Local state syncing
  // -----------------------------
  useEffect(() => {
    const normalizedPersonas = (personas || []).map((p) => ({
      ID: p.ID,
      Type: "persona",
      Title: p.Name,
    }));

    const normalizedAdresses = (adresses || []).map((a) => ({
      ID: a.ID,
      Type: "adress",
      Title: a.Title,
    }));

    setAllPersonasAndAdresses([...normalizedPersonas, ...normalizedAdresses]);
  }, [personas, adresses]);

  // Sync Firestore -> local drag array
  useEffect(() => {
    setDragArrayFormFields(formFields || []);
  }, [formFields]);

  // Keep formType in sync (important on refresh)
  useEffect(() => {
    if (!intakeForm || !intakeForm[0]) return;
    setFormType(intakeForm[0]?.FormType || null);
  }, [intakeForm]);

  // Update form title
  const titleHandler = (e) => {
    const docid = e.target.dataset.docid;
    const newTitle = e.target.value;

    db.collection("IntakeForms")
      .doc(docid)
      .update({ Title: newTitle })
      .then(() => setSaved("flex"));
  };

  // Existing items already in the form (kept)
  const existingDataItemIDs = useMemo(() => {
    if (!intakeForm || !intakeForm[0]?.DataItems) return [];
    return intakeForm[0].DataItems;
  }, [intakeForm]);

  // Add field
  const addField = async () => {
    try {
      await db.collection("IntakeFormFields").add({
        FormId: formId,
        Type: "",
        ID: uuid(),
        Compagny: client,
        CompagnyID: client,
        Timestamp: timestamp,
        Title: "",
        Position: (formFields?.length || 0) + 1,
        Required: "false",
      });

      setSaved("flex");
    } catch (err) {
      console.error("Failed to add fields to IntakeFormFields:", err);
    }
  };

  // Delete field (hard delete field, soft delete options)
  const deleteFormField = async (e) => {
    const docid = e.currentTarget.dataset.docid;
    const field = (dragArrayFormFields || []).find((f) => f.docid === docid);

    try {
      if (field?.ID) {
        await softDeleteMultipleOptionsByFieldId(field.ID);
        await softDeleteMatrixByFieldId(field.ID);
      }
      await db.collection("IntakeFormFields").doc(docid).delete();
      setSaved("flex");
    } catch (err) {
      console.error("Failed to delete IntakeFormField:", err);
    }
  };

  // Drag & drop reordering (persist Position)
  const handleDragEndFormFields = (result) => {
    if (!result.destination) return;

    const items = Array.from(dragArrayFormFields);
    const [movedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, movedItem);

    setDragArrayFormFields(items);

    items.forEach((it, idx) => {
      if (!it?.docid) return;
      db.collection("IntakeFormFields").doc(it.docid).update({ Position: idx + 1 });
    });

    setSaved("flex");
  };

  // Form type handler
  const formTypeHandler = async (e) => {
    const docid = e.target.dataset.docid;
    const newType = e.target.options[e.target.selectedIndex].value;

    await db
      .collection("IntakeForms")
      .doc(docid)
      .update({ FormType: newType })

    setFormType(newType);
  };

  // Form target handler
  const formTargetHandler = async (e) => {
    const docid = e.target.dataset.docid;
    const newTarget = e.target.options[e.target.selectedIndex].value;

    await db.collection("IntakeForms")
      .doc(docid)
      .update({ FormTarget: newTarget })
      setFormTarget(newTarget);
  };

  // Standard-form selected handler
  const formSelectedHandler = async (e) => {
    const docid = e.target.dataset.docid;
    const newSelected = e.target.options[e.target.selectedIndex].value;

    await db.collection("IntakeForms")
      .doc(docid)
      .update({ FormSelected: newSelected })

      setFormSelected(newSelected);

  };

  // Field title handler (AUTO: sets type + options + soft delete)
  const fieldTitleHandler = async (e) => {
    const docid = e.target.dataset.docid;
    const newTitle = e.target.value;

    const field = (dragArrayFormFields || []).find((f) => f.docid === docid);

    try {
      await applyAutoTypeRules({ fieldDocid: docid, field, nextTitle: newTitle });

      // Reflect immediately in UI (optimistic). Mirror applyAutoTypeRules:
      // only the "Type woning" special case changes Type.
      setDragArrayFormFields((prev) =>
        (prev || []).map((f) => {
          if (f.docid !== docid) return f;
          const wasHousing = normalize(f.Title) === HOUSING_TITLE;
          const isHousing = normalize(newTitle) === HOUSING_TITLE;
          let nextType = f.Type;
          if (isHousing) nextType = "multiple-one";
          else if (wasHousing) nextType = "text";
          return { ...f, Title: normalize(newTitle), Type: nextType };
        })
      );
    } catch (err) {
      console.error("Failed to update field title/type:", err);
    }
  };

  // Field type handler (manual select; respects soft delete + ensure options)
  // Vast gegeven van het huishouden waaraan deze vraag hangt (T13). Het
  // antwoord komt dan op de kaart 'Vaste gegevens' in plaats van als eigen
  // gegeven; de server (saveIntakeFormResponse) leest dit veld.
  // AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md
  const bindsFixedFields = formTarget !== "persona";
  const fixedFieldHandler = async (docid, key) => {
    await db.collection("IntakeFormFields").doc(docid).update({ FixedFieldKey: key });
    setSaved("flex");
  };

  const fieldTypeHandler = async (e) => {
    const docid = e.target.dataset.docid;
    const newType = e.target.value;

    const field = (dragArrayFormFields || []).find((f) => f.docid === docid);
    if (!docid) return;

    try {
      await db.collection("IntakeFormFields").doc(docid).update({ Type: newType });

      const isMultipleType = newType === "multiple-one" || newType === "multiple-many";
      if (!isMultipleType && field?.ID) {
        await softDeleteMultipleOptionsByFieldId(field.ID);
      }

      // Switching away from matrix retires its rows/columns, exactly as
      // switching away from multiple-* retires its answer options.
      if (newType !== "matrix" && field?.ID) {
        await softDeleteMatrixByFieldId(field.ID);
      }

      if (newType === "multiple-one" && normalize(field?.Title) === HOUSING_TITLE) {
        await ensureHousingTypeOptions(field);
      }

      setDragArrayFormFields((prev) =>
        (prev || []).map((f) => (f.docid === docid ? { ...f, Type: newType } : f))
      );

      setSaved("flex");
    } catch (err) {
      console.error("Failed to update field type:", err);
    }
  };


  return (
    <>
      <datalist id="form-field-item-titles">
        {(intakeForm?.[0]?.FormTarget === "adress" || !!formSelected) && <DatalistOptions />}
        {(intakeForm?.[0]?.FormTarget === "persona" || !!formSelected) && <DatalistOptionsPersonas />}
      </datalist>


      <div className="main">
        <div className="main-container">
          <PageHeader
            heading={"Intakeformulier instellingen"}
            subHeading={intakeForm && intakeForm[0]?.Title}
            description={``}
            AI={false}
          />

          <div className="intakeform-container">
            <ReadOnlyGuard edit="forms.edit">
              {(canEdit) => (
            intakeForm &&
              intakeForm.map((item) => (
                <div key={item.ID}>
                  <h2>Formulier titel</h2>
                  <input
                    type="text"
                    defaultValue={item.Title}
                    data-docid={item.docid}
                    onChange={titleHandler}
                  />
                  <div className="form-settings-container">
                    <h2>Instellingen</h2>

                    <div className="form-settings-item-container">
                      <div className="form-settings-item-title-container">
                        <h3>Intakeformulier/standaardformulier</h3>
                        <p>
                          {(() => {
                            const target = personasOn === 'flex' && housevisits
                              ? 'medewerkers en huishoudens'
                              : personasOn === 'flex'
                              ? 'medewerkers'
                              : 'huishoudens';
                            return `Een intakeformulier gebruik je om nieuwe ${target} aan te maken. Een standaard formulier gebruik je om de gegevens van bestaande ${target} aan te vullen.`;
                          })()}
                        </p>
                      </div>

                      <select
                        data-docid={item.docid}
                        onChange={formTypeHandler}
                        defaultValue={item.FormType || ""}
                      >
                        <option value="">-- Selecteer --</option>
                        <option value="intake">Intakeformulier</option>
                        <option value="standard">Standaardformulier</option>
                      </select>
                    </div>

                    {formType === "intake" && personasOn === 'flex' && housevisits && (
                      <div className="form-settings-item-container">
                        <div className="form-settings-item-title-container">
                          <h3>Medewerker/huishouden</h3>
                          <p>Selecteer of dit intakeformulier voor een nieuwe medewerker of een nieuw huishouden is.</p>
                        </div>

                        <select
                          data-docid={item.docid}
                          onChange={formTargetHandler}
                          defaultValue={item.FormTarget || ""}
                        >
                          <option value="">-- Selecteer --</option>
                          {personasOn === 'flex' && <option value="persona">Medewerker</option>}
                          {housevisits && <option value="adress">Huishouden</option>}
                        </select>
                      </div>
                    )}

                    {formType === "standard" && (
                      <div className="form-settings-item-container">
                        <div className="form-settings-item-title-container">
                          <h3>Medewerker/huishouden</h3>
                          <p>Selecteer de medewerker of huishouden waarvoor dit standaardformulier is.</p>
                        </div>

                        {/* Gestuurd, niet `defaultValue`: die leest React alleen bij het
                            aanhaken, en de medewerkers/huishoudens komen uit eigen
                            Firestore-reads die ná het formulier binnen kunnen zijn. Er is
                            dan geen optie om te kiezen, dus valt het menu terug op
                            '-- Selecteer --' en blijft daar staan.
                            AI-CONTEXT: See docs/pitfalls.md#ui-select-defaultvalue-loses-late-options */}
                        <select
                          data-docid={item.docid}
                          onChange={formSelectedHandler}
                          value={item.FormSelected || ""}
                        >
                          <option value="">-- Selecteer --</option>
                          {allPersonasAndAdresses
                            .filter((entity) => {
                              if (entity.Type === 'persona') return personasOn === 'flex';
                              if (entity.Type === 'adress') return housevisits;
                              return false;
                            })
                            .map((entity) => (
                              <option key={entity.ID} value={entity.ID}>
                                {entity.Title} - {entity.Type === "persona" ? "(Medewerker)" : "(Huishouden)"}
                              </option>
                            ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {formType && (formTarget || formSelected) && (
                    <div>

                      <div className="attached-data-items-container">
                        <h2>Invulvelden</h2>

                        <Table
                          columns={[
                            { key: 'drag', label: '', searchable: false },
                            { key: 'title', label: 'TITEL' },
                            { key: 'type', label: 'TYPE', searchable: false },
                            { key: 'options', label: 'ANTWOORDOPTIES', searchable: false },
                            ...(bindsFixedFields ? [{ key: 'fixed', label: 'VAST GEGEVEN', searchable: false }] : []),
                            { key: 'required', label: 'VERPLICHT', searchable: false },
                            { key: 'delete', label: 'VERWIJDEREN', searchable: false },
                          ]}
                          items={dragArrayFormFields || []}
                          draggable
                          droppableId={`intakeformfields-${formId}`}
                          onDragEnd={handleDragEndFormFields}
                          getItemKey={(field) => field.ID}
                          searchAccessor={(field) => field?.Title || ''}
                          searchPlaceholder="Zoeken op titel"
                          emptyMessage="Geen invulvelden gevonden."
                          addRow={canEdit ? {
                            toolTipContent: 'Veld toevoegen',
                            onClick: addField,
                          } : undefined}
                          renderRow={(field) => (
                            <>
                              <TableDragHandleCell className="attached-table-drag-handle">
                                <DragIndicatorOutlinedIcon />
                              </TableDragHandleCell>
                              <TableCell>
                                <input
                                  type="text"
                                  data-docid={field.docid}
                                  defaultValue={field.Title}
                                  onBlur={fieldTitleHandler}
                                  list="form-field-item-titles"
                                  placeholder="Kies of typ een titel"
                                  autoComplete="off"
                                />
                              </TableCell>
                              <TableCell>
                                <select
                                  data-docid={field.docid}
                                  value={field.Type || ""}
                                  onChange={fieldTypeHandler}
                                >
                                  <option value="">-- Selecteer --</option>
                                  {INTAKE_FORM_FIELD_TYPES.map((veldType) => (
                                    <option key={veldType.value} value={veldType.value}>
                                      {veldType.label}
                                    </option>
                                  ))}
                                </select>
                              </TableCell>
                              <TableCell>
                                {(field.Type === "multiple-one" || field.Type === "multiple-many") ? (
                                  <div>
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        minWidth: "22px",
                                        height: "22px",
                                        borderRadius: "999px",
                                        border: "1px solid rgba(0,0,0,0.2)",
                                        padding: "0 6px",
                                        fontSize: "0.85rem",
                                      }}
                                      title="Aantal opties"
                                    >
                                      <SupportFormOptionsCount fieldId={field.ID} />
                                    </span>

                                    {canEdit && (
                                      <Tooltip
                                        content={"Antwoordopties aanpassen"}
                                        top="-60px"
                                        width={"90px"}
                                      >
                                        <EditOutlinedIcon
                                          className="table-delete-icon"
                                          onClick={() => openOptionsModal(field)}
                                        />
                                      </Tooltip>
                                    )}
                                  </div>
                                ) : field.Type === "matrix" ? (
                                  canEdit && (
                                    <Tooltip
                                      content={"Rijen en kolommen aanpassen"}
                                      top="-60px"
                                      width={"90px"}
                                    >
                                      <EditOutlinedIcon
                                        className="table-delete-icon"
                                        onClick={() => openMatrixModal(field)}
                                      />
                                    </Tooltip>
                                  )
                                ) : (
                                  <span style={{ opacity: 0.6 }}>—</span>
                                )}
                              </TableCell>
                              {bindsFixedFields && (
                                <TableCell>
                                  {canEdit && (
                                    <FixedFieldSelect
                                      field={field}
                                      fields={formFields || []}
                                      onChange={(key) => fixedFieldHandler(field.docid, key)}
                                    />
                                  )}
                                </TableCell>
                              )}
                              <TableCell>
                                {canEdit && (
                                  <ToggleButton
                                    collection="IntakeFormFields"
                                    docid={field.docid}
                                    id="Required"
                                    value={field.Required}
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                {canEdit && (
                                  <DeleteOutlinedIcon
                                    className="table-delete-icon"
                                    data-docid={field.docid}
                                    onClick={deleteFormField}
                                  />
                                )}
                              </TableCell>
                            </>
                          )}
                        />
                      </div>
                    </div>
                  )}
                  <div className="form-settings-container">
                    <h2>Instellingen</h2>

                    <div className="form-settings-item-container">
                      <div className="form-settings-item-title-container">
                        <h3>Ik ben geen robot</h3>
                        <p>Voeg een captcha toe aan het formulier om spam te voorkomen.</p>
                      </div>
                      {canEdit && (
                        <ToggleButton
                          collection="IntakeForms"
                          docid={item.docid}
                          id="Captcha"
                          value={item.Captcha}
                        />
                      )}
                    </div>

                    <div className="form-settings-item-container">
                      <div className="form-settings-item-title-container">
                        <h3>Privacybeleid</h3>
                        <p>
                          Gebruikers moeten akkoord gaan met het privacybeleid
                          voordat ze het formulier kunnen insturen.
                        </p>
                        {privacyConfigured ? (
                          <p className="form-settings-hint">
                            De tekst beheer je in{" "}
                            <span
                              className="link-inline"
                              onClick={() => history.push(`/${client}/settings/6`)}
                            >
                              Instellingen → Ondersteuning
                            </span>
                            .
                          </p>
                        ) : (
                          <p className="form-settings-warning">
                            Stel eerst de privacybeleid-tekst in bij{" "}
                            <span
                              className="link-inline"
                              onClick={() => history.push(`/${client}/settings/6`)}
                            >
                              Instellingen → Ondersteuning
                            </span>
                            .
                          </p>
                        )}
                      </div>
                      {privacyConfigured && canEdit && (
                        <ToggleButton
                          collection="IntakeForms"
                          docid={item.docid}
                          id="PrivacyCheckbox"
                          value={item.PrivacyCheckbox}
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))
              )}
            </ReadOnlyGuard>
          </div>
        </div>
      </div>

      <Modal
        modalOpen={optionsModalOpen}
        setModalOpen={(v) => (v ? setOptionsModalOpen(true) : closeOptionsModal())}
        pageHeader={"Opties aanpassen"}
        confirm={() => closeOptionsModal()}
        content={<OptionsModalContent field={activeOptionsField} allowOther />}
      />

      <Modal
        modalOpen={matrixModalOpen}
        setModalOpen={(v) => (v ? setMatrixModalOpen(true) : closeMatrixModal())}
        pageHeader={"Rijen en kolommen aanpassen"}
        confirm={() => closeMatrixModal()}
        content={<IntakeFormMatrixEditor field={activeMatrixField} />}
      />
    </>
  );
};

export default IntakeFormBuilder;
